// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useEffect, useState } from 'react';

export const useSpeechVolume = (isEnabled: boolean): number => {
  const [volume, setVolume] = useState(0);

  useEffect(() => {
    if (!isEnabled) {
      setVolume(0);
    }
  }, [isEnabled]);

  useEffect(() => {
    const handleVolume = (volume: number): void => {
      const adjustedVolume = Math.floor(volume * 20) * 5;
      setVolume(adjustedVolume < 30 ? 0 : adjustedVolume);
    };

    let hasUnsubscribed = false;
    let unsubscribe: () => void | undefined;

    if (isEnabled) {
      subscribeToSpeechVolume(handleVolume).then((result) => {
        unsubscribe = result.unsubscribe;
        if (hasUnsubscribed) {
          unsubscribe();
        }
      });
    }

    return () => {
      hasUnsubscribed = true;
      unsubscribe?.();
    };
  }, [setVolume, isEnabled]);

  return volume;
};

export const subscribeToSpeechVolume = async (
  callback: (volume: number) => void
): Promise<{
  unsubscribe: () => void;
}> => {
  let audioContext: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let animationFrameId: number | null = null;

  const updateVolume = (): void => {
    if (!analyser) return;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);
    const volume = Math.max(...dataArray) / 255; // Normalize volume to 0-1 range
    callback(volume);
    animationFrameId = requestAnimationFrame(updateVolume);
  };

  const start = async (): Promise<void> => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Your browser does not support microphone access.');
    }

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(audioStream);
      analyser = audioContext.createAnalyser();

      analyser.fftSize = 256;
      source.connect(analyser);
      updateVolume();
    } catch (e) {
      console.error('Microphone access denied or not supported:', e);
    }
  };

  const stop = (): void => {
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
    if (analyser) analyser.disconnect();
    if (audioContext) audioContext.close();
    audioContext = null;
    analyser = null;
  };

  await start();

  return { unsubscribe: stop };
};
